import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import SectionPdfExport, { buildColumns } from '@/components/SectionPdfExport';

const fmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};

export default function SDECalculatorPage({ results, analysis }) {
  const sde = results?.sde_analysis || [];

  if (sde.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-zinc-500 text-sm" data-testid="sde-page">
        Import data to view SDE analysis
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sde-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">Seller's Discretionary Earnings (SDE)</h2>
          <p className="text-sm text-zinc-400 mt-1">Recalculated from P&L — shows true owner earnings via add-back waterfall</p>
        </div>
        <SectionPdfExport
          title="SDE Analysis"
          analysisName={analysis?.name}
          rows={sde}
          columns={buildColumns([
            { label: 'Period', key: 'label' },
            { label: 'Net Income', key: 'net_income', format: 'currency' },
            { label: 'Officer Salary', key: 'officer_salary', format: 'currency' },
            { label: 'Depreciation', key: 'depreciation', format: 'currency' },
            { label: 'Other Add-backs', key: 'other_addbacks', format: 'currency' },
            { label: 'SDE', key: 'sde', format: 'currency' },
          ])}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sde.map((entry, idx) => (
          <Card key={idx} className="bg-zinc-900/60 border-zinc-800/60">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">
                  {entry.label}
                </CardTitle>
                {entry.label?.includes('Average') && (
                  <Badge variant="outline" className="border-indigo-500/30 text-indigo-400 text-[10px]">AVG</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Waterfall */}
              <div className="space-y-0">
                <WaterfallRow label="Net Income" value={entry.net_income} isBase />
                {entry.officer_salary !== undefined && <WaterfallRow label="(+) Officer's Salary" value={entry.officer_salary} positive />}
                {entry.interest_expense !== undefined && entry.interest_expense !== 0 && <WaterfallRow label="(+) Interest Expense" value={entry.interest_expense} positive />}
                {entry.depreciation !== undefined && entry.depreciation !== 0 && <WaterfallRow label="(+) Depreciation" value={entry.depreciation} positive />}
                {entry.entertainment !== undefined && entry.entertainment !== 0 && <WaterfallRow label="(+) Entertainment/Perks" value={entry.entertainment} positive />}
                {entry.other_addbacks !== undefined && entry.other_addbacks !== 0 && <WaterfallRow label="(+) Other Add-Backs" value={entry.other_addbacks} positive />}

                <div className="border-t border-zinc-700 mt-2 pt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-zinc-200">Recalculated SDE</span>
                    <span className="font-mono text-lg font-bold text-indigo-400">{fmt(entry.sde)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* SDE Variance Note */}
      {sde.length >= 2 && (
        <Card className="bg-zinc-900/60 border-amber-500/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
              <div className="text-xs text-zinc-400">
                <span className="text-amber-400 font-medium">Note:</span> {sde[0]?.label} SDE is elevated due to high officer salary (
                {fmt(sde[0]?.officer_salary)}). {sde[1]?.label} officer salary dropped to {fmt(sde[1]?.officer_salary)}.
                The Deal Tab states SDE of $257,000 — verify the basis with seller. Use the 2-year average with caution if officer comp varies significantly.
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function WaterfallRow({ label, value, positive, isBase }) {
  if (value === undefined || value === null) return null;
  return (
    <div className="flex items-center justify-between py-1.5 text-xs">
      <span className="text-zinc-400">{label}</span>
      <span className={`font-mono ${isBase ? 'text-zinc-200' : positive ? 'text-emerald-400' : 'text-zinc-200'}`}>
        {positive && value > 0 ? '+' : ''}{fmt(value)}
      </span>
    </div>
  );
}
